import { DEFAULT_VALUES } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import { IBusinessUnitRow, IScoreData } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';
import { getDateTimestamp } from 'src/common/helpers/DateFormatHelper';

import { BusinessUnitsTableDataQuery } from '../BusinessUnitsTable.generated';

export interface IssuesPeriodCount {
	current: number;
	previous: number;
}

export interface SummaryIssuesData {
	current: number;
	previous: number;
	critical: IssuesPeriodCount;
}

export interface BusinessUnitsTableResult {
	rows: IBusinessUnitRow[];
	total: number;
	unfilteredTotal: number;
	scoreWebProducts: ScoreWebProduct[];
	aggregatedScoreWebProducts: AggregatedScoreWebProducts;
	aggregatedScoreDeltas: AggregatedScoreDeltaResults;
	summaryUniqueIssues: SummaryIssuesData;
	summaryTotalIssues: SummaryIssuesData;
}

export type ScoreWebProduct = NonNullable<
	NonNullable<BusinessUnitsTableDataQuery['score_web_products']>[number]
>;

type GroupedScoreWebProducts = Record<string, Record<string, Record<string, ScoreWebProduct[]>>>;

type AggregatedScoreEntry = {
	calculationDate: string;
	unitId: string;
	timeframeDays: string;
	totalUniqueIssues: number;
	totalUniqueCriticalIssues: number;
	totalIssues: number;
	totalCriticalIssues: number;
	weightedScore: number;
};

export type AggregatedScoreWebProducts = AggregatedScoreEntry[];

type AggregatedScoreDeltaEntry = {
	unitId: string;
	timeframeDays: string;
	latestCalculationDate: string;
	previousCalculationDate: string;
	latestScore: number;
	previousScore: number;
	deltaScore: number;
};

export type AggregatedScoreDeltaResults = AggregatedScoreDeltaEntry[];

const DEFAULT_SCORE_DATA: IScoreData = { value: 0, percentage: null };
const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

const UNKNOWN_CALCULATION_DATE_KEY = 'unknown';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface UnitScoreMetrics {
	uniqueIssues?: number;
	criticalIssues?: number;
	seriousIssues?: number;
	weightedScore?: number;
	deltaScore?: number;
}

const extractScoreWebProducts = (
	scoreWebProducts?: BusinessUnitsTableDataQuery['score_web_products']
): ScoreWebProduct[] =>
	scoreWebProducts?.filter((product): product is ScoreWebProduct => product !== null) ?? [];

const groupScoreWebProducts = (scoreWebProducts: ScoreWebProduct[]): GroupedScoreWebProducts => {
	const grouped: GroupedScoreWebProducts = {};

	scoreWebProducts.forEach((product) => {
		const unitId = product?.unit_id?.trim();
		if (!unitId) {
			return;
		}

		const dateKey = product.calculation_date ?? UNKNOWN_CALCULATION_DATE_KEY;
		const timeframeKey = String(product.timeframe_days ?? 0);

		if (!grouped[dateKey]) {
			grouped[dateKey] = {};
		}

		if (!grouped[dateKey][timeframeKey]) {
			grouped[dateKey][timeframeKey] = {};
		}

		if (!grouped[dateKey][timeframeKey][unitId]) {
			grouped[dateKey][timeframeKey][unitId] = [];
		}

		grouped[dateKey][timeframeKey][unitId].push(product);
	});

	return grouped;
};

const aggregateGroupedScoreWebProducts = (
	groupedScoreWebProducts: GroupedScoreWebProducts
): AggregatedScoreWebProducts => {
	const aggregated: AggregatedScoreWebProducts = [];

	Object.entries(groupedScoreWebProducts).forEach(([dateKey, timeframeGroups]) => {
		Object.entries(timeframeGroups).forEach(([timeframeKey, unitGroups]) => {
			Object.entries(unitGroups).forEach(([unitId, products]) => {
				const totalUniqueIssues = products.reduce(
					(sum, product) => sum + (product.unique_issues ?? 0),
					0
				);
				const totalUniqueCriticalIssues = products.reduce(
					(sum, product) => sum + (product.unique_issues_critical ?? 0),
					0
				);
				const totalIssues = products.reduce((sum, product) => sum + (product.total_issues ?? 0), 0);
				const totalCriticalIssues = products.reduce(
					(sum, product) => sum + (product.total_issues_critical ?? 0),
					0
				);
				const weightedScoreNumerator = products.reduce(
					(sum, product) => sum + (product.score ?? 0) * (product.unique_issues ?? 0),
					0
				);
				const weightedScore =
					totalUniqueIssues > 0 ? weightedScoreNumerator / totalUniqueIssues : 0;

				aggregated.push({
					calculationDate: dateKey,
					unitId,
					timeframeDays: timeframeKey,
					totalUniqueIssues,
					totalUniqueCriticalIssues,
					totalIssues,
					totalCriticalIssues,
					weightedScore
				});
			});
		});
	});

	return aggregated;
};

const getEntriesForTimeframe = (
	aggregatedEntries: AggregatedScoreWebProducts,
	timeframeKey: string
): AggregatedScoreWebProducts =>
	aggregatedEntries.filter((entry) => entry.timeframeDays === timeframeKey);

const getLatestCalculationDate = (entries: AggregatedScoreWebProducts): string | undefined =>
	entries.reduce<string | undefined>((latestDate, entry) => {
		if (!latestDate || getDateTimestamp(entry.calculationDate) > getDateTimestamp(latestDate)) {
			return entry.calculationDate;
		}
		return latestDate;
	}, undefined);

const getComparisonCalculationDate = (
	entries: AggregatedScoreWebProducts,
	currentCalculationDate: string | undefined,
	selectedPeriodDays: number
): string | undefined => {
	if (!currentCalculationDate) {
		return undefined;
	}

	const currentTimestamp = getDateTimestamp(currentCalculationDate);
	const targetTimestamp = currentTimestamp - selectedPeriodDays * ONE_DAY_MS;
	const candidateDates = [...new Set(entries.map((entry) => entry.calculationDate))]
		.filter((date) => date !== currentCalculationDate)
		.map((date) => ({
			date,
			distanceFromTarget: Math.abs(getDateTimestamp(date) - targetTimestamp)
		}))
		.filter(({ distanceFromTarget }) => distanceFromTarget <= ONE_DAY_MS)
		.sort((a, b) => a.distanceFromTarget - b.distanceFromTarget);

	return candidateDates[0]?.date;
};

const getEntriesByUnitForDate = (
	entries: AggregatedScoreWebProducts,
	calculationDate: string | undefined
): Map<string, AggregatedScoreEntry> => {
	const entriesByUnit = new Map<string, AggregatedScoreEntry>();
	if (!calculationDate) {
		return entriesByUnit;
	}

	entries
		.filter((entry) => entry.calculationDate === calculationDate)
		.forEach((entry) => entriesByUnit.set(entry.unitId, entry));

	return entriesByUnit;
};

const calculateDeltaScoreEntries = (
	currentEntriesByUnit: Map<string, AggregatedScoreEntry>,
	comparisonEntriesByUnit: Map<string, AggregatedScoreEntry>
): AggregatedScoreDeltaResults => {
	const deltas: AggregatedScoreDeltaResults = [];

	currentEntriesByUnit.forEach((latest, unitId) => {
		const previous = comparisonEntriesByUnit.get(unitId);
		if (!previous) {
			return;
		}

		deltas.push({
			unitId,
			timeframeDays: latest.timeframeDays,
			latestCalculationDate: latest.calculationDate,
			previousCalculationDate: previous.calculationDate,
			latestScore: latest.weightedScore,
			previousScore: previous.weightedScore,
			deltaScore: latest.weightedScore - previous.weightedScore
		});
	});

	return deltas;
};

const aggregateIssuesByUnit = (
	scoreWebProducts: ScoreWebProduct[],
	timeframeKey: string,
	currentCalculationDate: string | undefined
): { criticalMap: Map<string, number>; seriousMap: Map<string, number> } => {
	const criticalMap = new Map<string, number>();
	const seriousMap = new Map<string, number>();

	if (!currentCalculationDate) {
		return { criticalMap, seriousMap };
	}

	scoreWebProducts
		.filter((p) => String(p.timeframe_days ?? 0) === timeframeKey)
		.forEach((product) => {
			const unitId = product?.unit_id?.trim();
			if (!unitId) {
				return;
			}
			if (product.calculation_date !== currentCalculationDate) {
				return;
			}
			const critical = (criticalMap.get(unitId) ?? 0) + (product.total_issues_critical ?? 0);
			const serious = (seriousMap.get(unitId) ?? 0) + (product.total_issues_serious ?? 0);
			criticalMap.set(unitId, critical);
			seriousMap.set(unitId, serious);
		});

	return { criticalMap, seriousMap };
};

const getScoreDelta = (
	currentEntry: AggregatedScoreEntry | undefined,
	comparisonEntry: AggregatedScoreEntry | undefined
): number | undefined => {
	if (!comparisonEntry) {
		return undefined;
	}

	if ((currentEntry?.totalUniqueIssues ?? 0) === 0 && comparisonEntry.totalUniqueIssues > 0) {
		return -1;
	}

	return currentEntry ? currentEntry.weightedScore - comparisonEntry.weightedScore : undefined;
};

const buildUnitScoreMetrics = (
	currentEntriesByUnit: Map<string, AggregatedScoreEntry>,
	comparisonEntriesByUnit: Map<string, AggregatedScoreEntry>
): Map<string, UnitScoreMetrics> => {
	const unitMetrics = new Map<string, UnitScoreMetrics>();
	const unitIds = new Set([...currentEntriesByUnit.keys(), ...comparisonEntriesByUnit.keys()]);

	unitIds.forEach((unitId) => {
		const currentEntry = currentEntriesByUnit.get(unitId);
		const comparisonEntry = comparisonEntriesByUnit.get(unitId);
		unitMetrics.set(unitId, {
			uniqueIssues: currentEntry?.totalUniqueIssues ?? 0,
			weightedScore: currentEntry?.weightedScore,
			deltaScore: getScoreDelta(currentEntry, comparisonEntry)
		});
	});

	return unitMetrics;
};

const formatScoreData = (weightedScore?: number, deltaScore?: number): IScoreData => {
	if (weightedScore === undefined && deltaScore === undefined) {
		return DEFAULT_SCORE_DATA;
	}

	const scoreValue =
		weightedScore !== undefined ? roundToOneDecimal(Math.max(weightedScore * 100, 0)) : 0;
	const scorePercentage = deltaScore !== undefined ? roundToOneDecimal(deltaScore * 100) : null;

	return {
		value: scoreValue,
		percentage: scorePercentage
	};
};

const createBusinessUnitRow = (
	unit: BusinessUnit,
	criticalIssues: number,
	seriousIssues: number,
	unitMetrics?: UnitScoreMetrics
): IBusinessUnitRow => {
	const uniqueIssues =
		unitMetrics?.uniqueIssues !== undefined
			? unitMetrics.uniqueIssues
			: criticalIssues + seriousIssues;
	const score = formatScoreData(unitMetrics?.weightedScore, unitMetrics?.deltaScore);

	return {
		id: unit.id,
		name: unit.name,
		criticalIssues,
		seriousIssues,
		uniqueIssues,
		score,
		// Add flat numeric field for sorting
		scoreValue: score.value
	};
};

export const EMPTY_SUMMARY: SummaryIssuesData = {
	current: 0,
	previous: 0,
	critical: { current: 0, previous: 0 }
};

const computeSummaryIssues = (
	currentEntriesByUnit: Map<string, AggregatedScoreEntry>,
	comparisonEntriesByUnit: Map<string, AggregatedScoreEntry>,
	configuredUnitIds: Set<string>,
	getTotal: (entry: AggregatedScoreEntry) => number,
	getCritical: (entry: AggregatedScoreEntry) => number
): SummaryIssuesData => {
	let currentTotal = 0;
	let previousTotal = 0;
	let currentCritical = 0;
	let previousCritical = 0;

	configuredUnitIds.forEach((unitId) => {
		const currentEntry = currentEntriesByUnit.get(unitId);
		if (currentEntry) {
			currentTotal += getTotal(currentEntry);
			currentCritical += getCritical(currentEntry);
		}

		const comparisonEntry = comparisonEntriesByUnit.get(unitId);
		if (comparisonEntry) {
			previousTotal += getTotal(comparisonEntry);
			previousCritical += getCritical(comparisonEntry);
		}
	});

	return {
		current: currentTotal,
		previous: previousTotal,
		critical: { current: currentCritical, previous: previousCritical }
	};
};

export const transformBusinessUnitsTableResults = (
	rawResults: BusinessUnitsTableDataQuery,
	businessUnits: BusinessUnit[],
	selectedPeriodDays?: number,
	currentCalculationDateOverride?: string
): BusinessUnitsTableResult => {
	const scoreWebProducts = extractScoreWebProducts(rawResults?.score_web_products);
	const groupedScoreWebProducts = groupScoreWebProducts(scoreWebProducts);
	const aggregatedScoreWebProducts = aggregateGroupedScoreWebProducts(groupedScoreWebProducts);
	const timeframeKey = String(selectedPeriodDays ?? DEFAULT_VALUES.SELECTED_PERIOD);
	const timeframeEntries = getEntriesForTimeframe(aggregatedScoreWebProducts, timeframeKey);
	const currentCalculationDate = getLatestCalculationDate(timeframeEntries);
	const comparisonCalculationDate = getComparisonCalculationDate(
		timeframeEntries,
		currentCalculationDate,
		selectedPeriodDays ?? DEFAULT_VALUES.SELECTED_PERIOD
	);
	const currentEntriesByUnit = getEntriesByUnitForDate(timeframeEntries, currentCalculationDate);
	const comparisonEntriesByUnit = getEntriesByUnitForDate(
		timeframeEntries,
		comparisonCalculationDate
	);
	const aggregatedScoreDeltas = calculateDeltaScoreEntries(
		currentEntriesByUnit,
		comparisonEntriesByUnit
	);
	const unitScoreMetrics = buildUnitScoreMetrics(currentEntriesByUnit, comparisonEntriesByUnit);

	const { criticalMap, seriousMap } = aggregateIssuesByUnit(
		scoreWebProducts,
		timeframeKey,
		currentCalculationDate
	);

	const unitsWithData = new Set<string>();
	[...criticalMap.keys(), ...seriousMap.keys(), ...unitScoreMetrics.keys()].forEach((unitId) => {
		unitsWithData.add(unitId);
	});

	const rows: IBusinessUnitRow[] = businessUnits
		.filter((unit) => unitsWithData.has(unit.id))
		.map((unit) => {
			const criticalIssues = criticalMap.get(unit.id) ?? 0;
			const seriousIssues = seriousMap.get(unit.id) ?? 0;

			return createBusinessUnitRow(
				unit,
				criticalIssues,
				seriousIssues,
				unitScoreMetrics.get(unit.id)
			);
		});

	const configuredUnitIds = new Set(businessUnits.map((u) => u.id));
	const summaryCurrentCalculationDate = currentCalculationDateOverride ?? currentCalculationDate;
	const summaryComparisonCalculationDate = getComparisonCalculationDate(
		timeframeEntries,
		summaryCurrentCalculationDate,
		selectedPeriodDays ?? DEFAULT_VALUES.SELECTED_PERIOD
	);
	const summaryCurrentEntriesByUnit = getEntriesByUnitForDate(
		timeframeEntries,
		summaryCurrentCalculationDate
	);
	const summaryComparisonEntriesByUnit = getEntriesByUnitForDate(
		timeframeEntries,
		summaryComparisonCalculationDate
	);
	const summaryUniqueIssues = computeSummaryIssues(
		summaryCurrentEntriesByUnit,
		summaryComparisonEntriesByUnit,
		configuredUnitIds,
		(e) => e.totalUniqueIssues,
		(e) => e.totalUniqueCriticalIssues
	);
	const summaryTotalIssues = computeSummaryIssues(
		summaryCurrentEntriesByUnit,
		summaryComparisonEntriesByUnit,
		configuredUnitIds,
		(e) => e.totalIssues,
		(e) => e.totalCriticalIssues
	);

	return {
		rows,
		total: rows.length,
		unfilteredTotal: rows.length,
		scoreWebProducts,
		aggregatedScoreWebProducts,
		aggregatedScoreDeltas,
		summaryUniqueIssues,
		summaryTotalIssues
	};
};
